import { Plus } from "lucide-react";
import { useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/trpc";
import MerchantContext from "@/contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { RuleForm } from "./RuleForm";
import { RuleCard } from "./RuleCard";
import { EmployeeAssignment } from "./EmployeeAssignment";
import { AssignmentRuleset, Employee, RuleFormData } from "../types";
import { compactCitySelections, normalizeStoredZones } from "@/utils/zones";
import type { FilterGroup } from "@/utils/employeeGroups";

type HandoffRulesTabProps = {
  assignmentRulesets: AssignmentRuleset[];
  employees: Employee[];
  onCreateAssignmentRuleset: (
    name: string,
    ruleset: AssignmentRuleset["ruleset"],
    priority: number,
    ignorePastEmployee?: boolean
  ) => void;
  onUpdateAssignmentRuleset: (
    assignmentRulesetId: string,
    updates: AssignmentRuleset["ruleset"],
    name?: string,
    priority?: number,
    ignorePastEmployee?: boolean
  ) => void;
  onAddEmployeesToAssignmentRuleset: (assignmentRulesetId: string, employeeIds: string[]) => void;
  onRemoveEmployeesFromAssignmentRuleset: (
    assignmentRulesetId: string,
    employeeIds: string[]
  ) => void;
  cityNeighborhoods: { city: string; neighborhoods: string[] }[];
  transferCategories: { name: string; description: string }[];
  branches: string[];
  pipelines?: Array<{ id: string; name: string }>;
};

const emptyRuleForm: RuleFormData = {
  name: "",
  zones: [],
  selectedZones: [],
  priceMin: undefined,
  priceMax: undefined,
  department: "",
  daysMin: undefined,
  daysMax: undefined,
  appointmentType: "",
  branchName: "",
  propertyTypes: [],
  transferCategories: [],
  matchListingOwner: false,
  ignorePastEmployee: false,
  priority: 50,
  pipelineId: "",
  listingSource: "",
};

const hasAnyCriterion = (form: RuleFormData, selectedZones: string[]): boolean =>
  selectedZones.length > 0 ||
  form.priceMin !== undefined ||
  form.priceMax !== undefined ||
  !!form.department ||
  (form.daysMin !== undefined && form.daysMax !== undefined) ||
  !!form.appointmentType ||
  !!form.branchName ||
  !!form.pipelineId ||
  !!form.listingSource ||
  form.propertyTypes.length > 0 ||
  form.transferCategories.length > 0 ||
  form.matchListingOwner;

const isPriceRangeInverted = (form: RuleFormData): boolean =>
  form.priceMin !== undefined &&
  form.priceMax !== undefined &&
  form.priceMin > form.priceMax;

const buildRulesetPayload = (
  form: RuleFormData,
  selectedZones: string[]
): AssignmentRuleset["ruleset"] => {
  const ruleset: AssignmentRuleset["ruleset"] = {};

  if (selectedZones.length > 0) ruleset.zones = selectedZones;
  if (form.priceMin !== undefined || form.priceMax !== undefined) {
    ruleset.price = {
      ...(form.priceMin !== undefined ? { min: form.priceMin } : {}),
      ...(form.priceMax !== undefined ? { max: form.priceMax } : {}),
    };
  }
  if (form.department) ruleset.department = form.department;
  if (form.daysMin !== undefined && form.daysMax !== undefined) {
    ruleset.daysUntilMoveIn = { min: form.daysMin, max: form.daysMax };
  }
  if (form.appointmentType) ruleset.appointmentType = form.appointmentType;
  if (form.branchName) ruleset.branchName = form.branchName;
  if (form.pipelineId) ruleset.pipelineId = form.pipelineId;
  if (form.listingSource) ruleset.listingSource = form.listingSource;
  if (form.propertyTypes.length > 0) ruleset.propertyTypes = form.propertyTypes;
  if (form.transferCategories.length > 0) ruleset.transferCategories = form.transferCategories;
  if (form.matchListingOwner) ruleset.matchListingOwner = form.matchListingOwner;

  return ruleset;
};

export const HandoffRulesTab = (input: HandoffRulesTabProps) => {
  const { merchantId } = useContext(MerchantContext);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editForm, setEditForm] = useState<RuleFormData>(emptyRuleForm);
  const [isSaving, setIsSaving] = useState(false);

  const editableRules = input.assignmentRulesets.filter((r) => !r.isFallback);
  const fallbackRule = input.assignmentRulesets.find((r) => r.isFallback);

  // Grupos double as presets in the asesor picker, so "agregá al equipo de
  // Ventas Norte" is one click instead of ten. Queried here rather than drilled
  // through HandoffManagement, which already passes ~25 props — same call the
  // Tablero filter and GruposAsesoresTab make, so react-query serves it warm.
  const employeeGroups = trpc.employeeGroups.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );

  const groupPresets: FilterGroup[] = useMemo(
    () =>
      (employeeGroups.data ?? []).map((group) => ({
        id: group.id,
        label: group.name,
        employeeIds: group.employeeIds ?? [],
      })),
    [employeeGroups.data]
  );

  const isSheetOpen = isCreating || editingRuleId !== null;
  const sheetTitle = isCreating ? "Crear nueva regla" : "Editar regla";

  const resetEditForm = () => setEditForm(emptyRuleForm);

  const closeSheet = () => {
    if (isSaving) return;
    setEditingRuleId(null);
    setIsCreating(false);
    resetEditForm();
  };

  const handleEditRule = (rule: AssignmentRuleset) => {
    setEditingRuleId(rule.id);
    setIsCreating(false);

    const existingZones = rule.ruleset?.zones || [];
    const selectedZones = normalizeStoredZones(existingZones, input.cityNeighborhoods);

    setEditForm({
      name: rule.name || "",
      zones: existingZones,
      selectedZones,
      priceMin: rule.ruleset?.price?.min,
      priceMax: rule.ruleset?.price?.max,
      department: rule.ruleset?.department || "",
      daysMin: rule.ruleset?.daysUntilMoveIn?.min,
      daysMax: rule.ruleset?.daysUntilMoveIn?.max,
      appointmentType: rule.ruleset?.appointmentType || "",
      branchName: rule.ruleset?.branchName || "",
      propertyTypes: rule.ruleset?.propertyTypes || [],
      transferCategories: rule.ruleset?.transferCategories || [],
      matchListingOwner: rule.ruleset?.matchListingOwner || false,
      ignorePastEmployee: rule.ignorePastEmployee || false,
      priority: rule.priority || 50,
      pipelineId: rule.ruleset?.pipelineId || "",
      listingSource: rule.ruleset?.listingSource || "",
    });
  };

  const handleCityToggle = (city: string) => {
    setEditForm((prev) => {
      const cityData = input.cityNeighborhoods.find((c) => c.city === city);
      if (!cityData) return prev;

      const zoneSet = new Set(prev.selectedZones);
      const cityNeighborhoodZones = cityData.neighborhoods.map((n) => `${city}-${n}`);
      const isCityMacroSelected = zoneSet.has(city);
      const hasAllNeighborhoodsSelected =
        cityNeighborhoodZones.length > 0 &&
        cityNeighborhoodZones.every((zone) => zoneSet.has(zone));
      const isSelected = isCityMacroSelected || hasAllNeighborhoodsSelected;

      if (isSelected) {
        zoneSet.delete(city);
        cityNeighborhoodZones.forEach((zone) => zoneSet.delete(zone));
        return { ...prev, selectedZones: Array.from(zoneSet) };
      }

      zoneSet.add(city);
      cityNeighborhoodZones.forEach((zone) => zoneSet.delete(zone));
      return {
        ...prev,
        selectedZones: compactCitySelections(Array.from(zoneSet), input.cityNeighborhoods),
      };
    });
  };

  const handleNeighborhoodToggle = (city: string, neighborhood: string) => {
    setEditForm((prev) => {
      const zoneToToggle = `${city}-${neighborhood}`;
      const cityData = input.cityNeighborhoods.find((c) => c.city === city);
      if (!cityData) return prev;

      const cityNeighborhoodZones = cityData.neighborhoods.map((n) => `${city}-${n}`);
      const zoneSet = new Set(prev.selectedZones);
      const isCityMacroSelected = zoneSet.has(city);

      if (isCityMacroSelected) {
        zoneSet.delete(city);
        cityNeighborhoodZones.forEach((zone) => zoneSet.add(zone));
      }

      const isSelected = zoneSet.has(zoneToToggle);
      if (isSelected) {
        zoneSet.delete(zoneToToggle);
        return {
          ...prev,
          selectedZones: compactCitySelections(Array.from(zoneSet), input.cityNeighborhoods),
        };
      }

      zoneSet.add(zoneToToggle);
      return {
        ...prev,
        selectedZones: compactCitySelections(Array.from(zoneSet), input.cityNeighborhoods),
      };
    });
  };

  const getSelectedZones = () =>
    compactCitySelections([...editForm.selectedZones], input.cityNeighborhoods);

  const handleSaveEdit = async () => {
    if (!editingRuleId) return;
    setIsSaving(true);
    try {
      await input.onUpdateAssignmentRuleset(
        editingRuleId,
        buildRulesetPayload(editForm, getSelectedZones()),
        editForm.name.trim(),
        editForm.priority,
        editForm.ignorePastEmployee
      );
      setEditingRuleId(null);
      setIsCreating(false);
      resetEditForm();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "No pudimos guardar la regla";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreateRule = async () => {
    setIsSaving(true);
    try {
      await input.onCreateAssignmentRuleset(
        editForm.name.trim(),
        buildRulesetPayload(editForm, getSelectedZones()),
        editForm.priority,
        editForm.ignorePastEmployee
      );
      setEditingRuleId(null);
      setIsCreating(false);
      resetEditForm();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "No pudimos crear la regla";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    setEditingRuleId(null);
    resetEditForm();
  };

  /**
   * The fallback rule is the only one that must never run dry — it catches every
   * lead no other rule claims. Stated against the membership a change would
   * produce, so the one rule covers removing a single chip and saving a picker
   * session that adds some asesores while dropping others.
   */
  const validateFallbackMembership = (nextEmployeeIds: string[]): string | null => {
    const hasActive = nextEmployeeIds.some(
      (employeeId) =>
        input.employees.find((emp) => emp.id === employeeId)?.status === "active"
    );
    return hasActive ? null : "La regla de último recurso necesita al menos un agente activo.";
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-3 md:gap-0">
        <h2 className="text-lg md:text-xl font-semibold text-foreground">
          Reglas de asignación ({editableRules.length})
        </h2>
        <Button
          onClick={handleStartCreate}
          className="w-full md:w-auto min-h-11"
        >
          <Plus className="size-4" />
          Crear regla
        </Button>
      </div>

      {/* Rules list */}
      {editableRules.length > 0 ? (
        <div className="rounded-lg border">
          <div className="flex flex-col">
            {editableRules.map((assignmentRuleset, index) => (
              <div
                key={assignmentRuleset.name}
                className={
                  index < editableRules.length - 1 ? "border-b border-border p-4" : "p-4"
                }
              >
                <RuleCard
                  assignmentRuleset={assignmentRuleset}
                  employees={input.employees}
                  onEditRule={handleEditRule}
                  onAddEmployees={input.onAddEmployeesToAssignmentRuleset}
                  onRemoveEmployees={input.onRemoveEmployeesFromAssignmentRuleset}
                  pipelines={input.pipelines}
                  employeeGroups={groupPresets}
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Aún no tenés reglas. Creá una para elegir qué lead va a qué agente.
          </p>
        </div>
      )}

      {/* Fallback section */}
      {fallbackRule && (
        <section className="rounded-lg border bg-muted/50 p-4">
          <div className="flex flex-col gap-1 mb-3">
            <h3 className="text-base font-semibold text-foreground">
              Si ninguna regla coincide
            </h3>
            <p className="text-xs text-muted-foreground">
              Estos agentes reciben los leads que no coinciden con ninguna de tus reglas.
              Siempre debe haber al menos un agente activo.
            </p>
          </div>
          <EmployeeAssignment
            assignedEmployees={fallbackRule.assignedEmployees}
            availableEmployees={input.employees}
            groups={groupPresets}
            onAddEmployees={(employeeIds) =>
              input.onAddEmployeesToAssignmentRuleset(fallbackRule.id, employeeIds)
            }
            onRemoveEmployees={(employeeIds) =>
              input.onRemoveEmployeesFromAssignmentRuleset(fallbackRule.id, employeeIds)
            }
            validateChange={validateFallbackMembership}
          />
        </section>
      )}

      {/* Edit / Create Sheet */}
      <Sheet open={isSheetOpen} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent
          side="right"
          className="z-[1100] flex w-full flex-col gap-0 p-0 sm:max-w-none md:max-w-2xl"
        >
          <SheetHeader className="border-b border-border p-4 md:p-6">
            <SheetTitle className="text-left">{sheetTitle}</SheetTitle>
            <SheetDescription className="text-left">
              Definí a qué agente se asignan los leads que cumplan estos criterios.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-4 md:p-6">
            {isSheetOpen && !hasAnyCriterion(editForm, getSelectedZones()) && (
              <div className="mb-4 rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                Esta regla no tiene criterios — coincidirá con todos los leads que lleguen al
                pipeline.
              </div>
            )}
            <RuleForm
              formData={editForm}
              onFormChange={(updates) => setEditForm((prev) => ({ ...prev, ...updates }))}
              cityNeighborhoods={input.cityNeighborhoods}
              onCityToggle={handleCityToggle}
              onNeighborhoodToggle={handleNeighborhoodToggle}
              getSelectedZones={getSelectedZones}
              transferCategories={input.transferCategories}
              branches={input.branches}
              pipelines={input.pipelines}
              nameError={!editForm.name.trim()}
              priceRangeError={isPriceRangeInverted(editForm)}
            />
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-border bg-background p-4 md:p-6">
            <Button
              variant="outline"
              onClick={closeSheet}
              disabled={isSaving}
              className="min-h-11"
            >
              Cancelar
            </Button>
            <Button
              onClick={isCreating ? handleCreateRule : handleSaveEdit}
              disabled={
                isSaving || !editForm.name.trim() || isPriceRangeInverted(editForm)
              }
              className="min-h-11"
            >
              {isSaving
                ? "Guardando…"
                : isCreating
                  ? "Crear regla"
                  : "Guardar cambios"}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};
